import React, { useState, useEffect } from 'react'
import { AircraftTransaction, Option } from '../../types/database'
import { TransactionOverridesNewService } from '../../services/transaction-overrides-new.service'
import { supabase } from '../../lib/supabase'
import { formatPrice } from '../../utils/formatters'

interface TransactionOptionsProps {
  transaction: AircraftTransaction
  modelId: number
  onUpdate: (updatedTransaction: AircraftTransaction) => void
  readonly?: boolean
  hasEditAccess?: boolean
}

export const TransactionOptions: React.FC<TransactionOptionsProps> = ({
  transaction,
  modelId,
  onUpdate,
  readonly = false,
  hasEditAccess = true
}) => {
  const [options, setOptions] = useState<Option[]>([])
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const [originalOptions, setOriginalOptions] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetchOptions()
    loadEffectiveOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, transaction])

  const loadEffectiveOptions = async () => {
    try {
      // Try to get override data first
      const override = await TransactionOverridesNewService.getByTransactionId(transaction.transaction_id)

      let effectiveOptions: string[]
      if (override && override.options) {
        // Use override options if they exist
        effectiveOptions = override.options
      } else {
        // Fall back to transaction snapshot options
        effectiveOptions = transaction.options || []
      }

      setSelectedOptions(effectiveOptions)
      setOriginalOptions(effectiveOptions)
    } catch (err) {
      // Fall back to transaction snapshot options
      const fallbackOptions = transaction.options || []
      setSelectedOptions(fallbackOptions)
      setOriginalOptions(fallbackOptions)
    }
  }

  const fetchOptions = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('options')
        .select('*')
        .eq('model_id', modelId)
        .order('name')

      if (error) throw error
      setOptions(data || [])
    } catch (err) {
      // Error fetching options
    } finally {
      setLoading(false)
    }
  }

  const handleToggleOption = (optionId: string) => {
    if (readonly || !hasEditAccess) return

    setSelectedOptions(prev => {
      if (prev.includes(optionId)) {
        return prev.filter(id => id !== optionId)
      } else {
        return [...prev, optionId]
      }
    })
  }

  const handleSave = async () => {
    try {
      setSaving(true)

      // Use the new transaction overrides service to save options
      await TransactionOverridesNewService.upsert({
        transaction_id: transaction.transaction_id,
        options: selectedOptions
      })

      // Update the original options to reflect the saved state
      setOriginalOptions(selectedOptions)

      // Update the local transaction state to reflect the changes
      const updatedTransaction = { ...transaction, options: selectedOptions }
      onUpdate(updatedTransaction)
      alert('Options saved successfully!')
    } catch (err: any) {
      alert('Failed to save options: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const calculateTotalValue = () => {
    return selectedOptions.reduce((total, optionId) => {
      const option = options.find(o => o.option_id === optionId)
      return total + (option?.value || 0)
    }, 0)
  }

  const hasChanges = () => {
    return JSON.stringify(selectedOptions.sort()) !== JSON.stringify(originalOptions.sort())
  }

  if (loading) {
    return (
      <div className="bg-gray-50 rounded-xl shadow-lg p-6 border-2 border-gray-300">
        <div className="text-gray-600">Loading options...</div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 rounded-xl shadow-lg p-6 border-2 border-gray-300">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Transaction Options Configuration
        </h3>
        {!readonly && hasEditAccess && hasChanges() && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Options'}
          </button>
        )}
      </div>

      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Changes to options will only affect this transaction snapshot, 
          not the original aircraft record.
        </p>
      </div>

      {options.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No options configured for this aircraft model.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {options.map((option) => {
              const isSelected = selectedOptions.includes(option.option_id)
              return (
                <div
                  key={option.option_id}
                  className={`
                    p-3 rounded-lg border-2 cursor-pointer transition-all duration-200
                    ${isSelected 
                      ? 'border-purple-500 bg-purple-50 shadow-md' 
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                    }
                    ${readonly || !hasEditAccess ? 'cursor-not-allowed opacity-60' : ''}
                  `}
                  onClick={() => handleToggleOption(option.option_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 text-sm">
                        {option.name}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {formatPrice(option.value?.toString())}
                      </p>
                    </div>
                    <div className={`
                      w-5 h-5 rounded border-2 flex items-center justify-center
                      ${isSelected 
                        ? 'border-purple-500 bg-purple-500' 
                        : 'border-gray-300'
                      }
                    `}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{selectedOptions.length}</span> of {options.length} options selected
              </div>
              <div className="text-lg font-semibold text-gray-900">
                Total Value: {formatPrice(calculateTotalValue().toString())}
              </div>
            </div>
          </div>

          {readonly && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm text-yellow-800">
                <strong>Read-only mode:</strong> You cannot modify options for this transaction.
              </p>
            </div>
          )}

          {!hasEditAccess && !readonly && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">
                <strong>No edit access:</strong> You don't have permission to modify options for this market.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
